import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { AgentState, UnavailableCode } from '../../types';
import { useCallTimer } from '../../hooks/useCallTimer';
import { logger } from '../../utils/logger';
import { AgentStateDropdown } from './AgentStateDropdown';
import styles from './AgentStateSelectorButton.module.css';

interface AgentStateSelectorProps {
  currentState: AgentState;
  onStateChange: (state: string, reasonCode?: string) => void;
  disabled?: boolean;
  unavailableCodes?: UnavailableCode[];
}

export function AgentStateSelector({
  currentState,
  onStateChange,
  disabled = false,
  unavailableCodes = [],
}: AgentStateSelectorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();

  const { formattedDuration } = useCallTimer(currentState.stateStartTime);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const getDisplayName = useCallback((): string => {
    if (currentState.state === 'Unavailable') {
      return t('settings:agentState.unavailable');
    }
    return currentState.state;
  }, [currentState, t]);

  const handleStateSelect = useCallback(
    async (state: string, reasonCode?: string) => {
      setIsLoading(true);
      setIsOpen(false);

      try {
        logger.debug('[AgentState] Setting state:', state, reasonCode);
        await onStateChange(state, reasonCode);
      } catch (error) {
        logger.error('[AgentState] Failed to set state:', error);
      } finally {
        setIsLoading(false);
      }
    },
    [onStateChange]
  );

  const getStateColor = (state: string): string => {
    switch (state) {
      case 'Available':
        return styles.stateAvailable;
      case 'Unavailable':
        return styles.stateUnavailable;
      case 'Working':
      case 'ACW':
        return styles.stateWorking;
      case 'InboundContact':
      case 'OutboundContact':
        return styles.stateOnCall;
      default:
        return styles.stateUnknown;
    }
  };

  return (
    <div className={styles.container} ref={containerRef}>
      <button
        className={`${styles.stateButton} ${getStateColor(currentState.state)}`}
        onClick={() => setIsOpen(!isOpen)}
        disabled={disabled || isLoading}
      >
        <span className={styles.stateIndicator} />
        <span className={styles.stateText}>{getDisplayName()}</span>
        {currentState.stateStartTime && (
          <span className={styles.stateTimer}>{formattedDuration}</span>
        )}
        <svg
          className={`${styles.chevron} ${isOpen ? styles.chevronOpen : ''}`}
          viewBox="0 0 24 24"
          width="16"
          height="16"
        >
          <path fill="currentColor" d="M7 10l5 5 5-5z" />
        </svg>
      </button>

      {isOpen && (
        <AgentStateDropdown unavailableCodes={unavailableCodes} onStateSelect={handleStateSelect} />
      )}
    </div>
  );
}

export default AgentStateSelector;
